import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { NAuthClientError, NAuthErrorCode } from '@nauth-toolkit/client';
import { Button, Panel } from '../../components';
import { passwordPolicyIssues } from './api';
import { useConfirmPasswordReset } from './useAuth';
import { safeNextPath } from '../../lib/nextPath';

/**
 * nauth answers `forgot-password/confirm` with one of a fixed set of codes
 * (see confirmForgotPassword() in auth.service.js). Mapped by hand rather than
 * shown verbatim: nauth's own message text is fine for a log line but reads as
 * an internal error code on a form ("PASSWORD_RESET_CODE_EXPIRED").
 */
function errorMessageFor(error: unknown): string {
  if (!(error instanceof NAuthClientError) || error.isNetworkError) {
    return 'Could not reach the server. Try again.';
  }
  switch (error.code) {
    case NAuthErrorCode.PASSWORD_RESET_CODE_INVALID:
      return 'That code is not valid. Double-check the email, or request a new one.';
    case NAuthErrorCode.PASSWORD_RESET_CODE_EXPIRED:
      return 'That code has expired. Request a new one below.';
    case NAuthErrorCode.PASSWORD_RESET_MAX_ATTEMPTS:
      return 'Too many attempts with this code. Request a new one below.';
    case NAuthErrorCode.AUTH_ACCOUNT_LOCKED:
      return 'This account is locked. Contact your project lead.';
    case NAuthErrorCode.SIGNUP_WEAK_PASSWORD:
      return 'That password does not meet the requirements below.';
    case NAuthErrorCode.PASSWORD_REUSED:
      return 'That was one of your recent passwords. Choose a different one.';
    default:
      return error.message;
  }
}

/**
 * Step two: prove possession of the emailed code and set a new password.
 *
 * Takes `identifier` and (if a link-based flow is ever wired up server-side —
 * see the note on confirmPasswordReset in ./api) `code` from the query string,
 * so a page reached by pasting a code in by hand and a page reached by
 * clicking a future link behave the same way. Neither is required to be
 * present: both fields stay plain, editable inputs either way.
 *
 * Does not sign the user in on success. `forgot-password/confirm` returns no
 * tokens — it only proves the code and sets the password — so the natural next
 * step is the ordinary sign-in form, where the user proves the new credential
 * exactly like anyone else.
 */
export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const next = safeNextPath(searchParams.get('next'));
  const confirmReset = useConfirmPasswordReset();

  const [identifier, setIdentifier] = useState(searchParams.get('identifier') ?? '');
  const [code, setCode] = useState(searchParams.get('code') ?? '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const policyIssues = passwordPolicyIssues(newPassword);
  const passwordsMatch = newPassword === confirmPassword;
  const canSubmit =
    identifier.trim() !== '' && code.trim() !== '' && policyIssues.length === 0 && passwordsMatch;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;
    confirmReset.mutate({ identifier: identifier.trim(), code: code.trim(), newPassword });
  };

  return (
    <main className="relative flex min-h-[100dvh] items-center justify-center overflow-hidden bg-surface px-4 py-10">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-0 h-[26.25rem] w-[45rem] -translate-x-1/2 -translate-y-1/3 rounded-full bg-accent-tint blur-3xl"
      />

      <div className="relative w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center text-center">
          <h1 className="sr-only">Set a new Rundown password</h1>
          <img src="/rundown-logo.png" alt="Rundown — AI project management" className="h-20 w-auto" />
          <p className="mt-4 fs-13 text-muted">The shared portal for your project.</p>
        </div>

        <Panel className="shadow-sm">
          {confirmReset.isSuccess ? (
            <div className="flex flex-col gap-4 text-center">
              <p className="fs-14 font-semibold text-ink">Password updated</p>
              <p className="fs-13 leading-relaxed text-muted">Sign in with your new password to continue.</p>
              <Link
                to={`/signin?next=${encodeURIComponent(next)}`}
                className="fs-13 font-semibold text-accent hover:underline"
              >
                Go to sign in →
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              <p className="mb-1 fs-13 text-muted">
                Enter the code we emailed you and choose a new password.
              </p>

              <label className="flex flex-col gap-1">
                <span className="fs-12 font-medium text-ink-soft">Email</span>
                <input
                  type="email"
                  autoComplete="email"
                  required
                  value={identifier}
                  onChange={(event) => setIdentifier(event.target.value)}
                  className="h-10 rounded-lg border border-line-stronger bg-base px-3 text-sm text-ink placeholder:text-faint transition-colors focus:border-accent"
                  placeholder="you@company.com"
                />
              </label>

              <label className="flex flex-col gap-1">
                <span className="fs-12 font-medium text-ink-soft">Verification code</span>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  className="h-10 rounded-lg border border-line-stronger bg-base px-3 text-sm tracking-widest text-ink placeholder:text-faint transition-colors focus:border-accent"
                  placeholder="123456"
                />
              </label>

              <label className="flex flex-col gap-1">
                <span className="fs-12 font-medium text-ink-soft">New password</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  required
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  className="h-10 rounded-lg border border-line-stronger bg-base px-3 text-sm text-ink placeholder:text-faint transition-colors focus:border-accent"
                  placeholder="••••••••••••"
                />
              </label>

              <label className="flex flex-col gap-1">
                <span className="fs-12 font-medium text-ink-soft">Confirm new password</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  className="h-10 rounded-lg border border-line-stronger bg-base px-3 text-sm text-ink placeholder:text-faint transition-colors focus:border-accent"
                  placeholder="••••••••••••"
                />
                {!passwordsMatch && confirmPassword !== '' && (
                  <span className="fs-11 text-danger">Passwords don&apos;t match.</span>
                )}
              </label>

              {newPassword !== '' && policyIssues.length > 0 && (
                <p className="fs-11 text-muted">Still needs: {policyIssues.join(', ')}.</p>
              )}

              {confirmReset.isError && (
                <p role="alert" className="rounded-md bg-danger-tint px-3 py-2 fs-13 text-danger">
                  {errorMessageFor(confirmReset.error)}
                </p>
              )}

              <Button
                type="submit"
                variant="secondary"
                disabled={!canSubmit || confirmReset.isPending}
                className="mt-1 h-10 w-full"
              >
                {confirmReset.isPending ? 'Updating…' : 'Update password'}
              </Button>
            </form>
          )}
        </Panel>

        <p className="mt-6 text-center fs-12 text-faint">
          <Link
            to={`/forgot-password?next=${encodeURIComponent(next)}`}
            className="hover:text-muted hover:underline"
          >
            Send a new code
          </Link>
        </p>
      </div>
    </main>
  );
}
